/**
 * Password visibility toggle functionality
 * Automatically finds password input fields with adjacent toggle buttons
 * and sets up event handling to show/hide password text
 */
export class PasswordToggle {
    /**
     * Initialize password toggle functionality across the document
     */
    public static initialize(): void {
        document.addEventListener('DOMContentLoaded', () => {
            // Look for all password input fields
            const passwordFields = document.querySelectorAll('input[type="password"]');

            passwordFields.forEach(passwordField => {
                // Find the closest parent input-group that might contain the toggle button
                const inputGroup = passwordField.closest('.input-group');
                if (!inputGroup) return;

                // Find a button within the same input-group
                const toggleButton = inputGroup.querySelector('button');
                if (!toggleButton) return;

                // Set up the event listener for the toggle button
                new PasswordToggle(passwordField as HTMLInputElement, toggleButton);
            });
        });
    }

    private passwordField: HTMLInputElement;
    private toggleButton: HTMLButtonElement;
    private announceElement: HTMLElement | null = null;
    private isVisible: boolean = false;

    /**
     * Create a new password toggle for a specific field/button pair
     */
    constructor(passwordField: HTMLInputElement, toggleButton: HTMLButtonElement) {
        this.passwordField = passwordField;
        this.toggleButton = toggleButton;

        // Find the announce element for this password field
        this.announceElement = document.getElementById('password-announce');

        // Set initial button text
        this.updateButtonText();

        // Add event listener
        this.toggleButton.addEventListener('click', this.toggleVisibility.bind(this));
    }

    /**
     * Toggle password visibility and update button text
     */
    private toggleVisibility(event: MouseEvent): void {
        event.preventDefault();

        this.isVisible = !this.isVisible;
        this.passwordField.type = this.isVisible ? 'text' : 'password';
        this.updateButtonText();
    }

    /**
     * Update the button text based on current visibility state
     */
    private updateButtonText(): void {
        this.toggleButton.textContent = this.isVisible ? 'Hide' : 'Show';
        this.toggleButton.setAttribute('aria-label', this.isVisible ? 'Hide password' : 'Show password');

        // Announce password visibility change to screen readers
        if (this.announceElement) {
            this.announceElement.setAttribute("aria-live", "assertive");
            this.announceElement.textContent = this.isVisible ? 'Your password is shown!' : 'Your password is hidden!';
        }
    }
}

// Initialize the password toggle functionality
PasswordToggle.initialize();